import { useState, useEffect } from "react";
import { Box, Typography, Paper, Stack, Button, Tooltip } from "@mui/material";
import ShieldIcon       from "@mui/icons-material/Shield";
import GavelIcon        from "@mui/icons-material/Gavel";
import RuleIcon         from "@mui/icons-material/Rule";
import WhiteList          from "./WhiteList";
import ResponseIfaceCard  from "./ResponseIfaceCard";
import { SuppressionSetting } from "../../../components/suppressionSettings";
import SuppressionView    from "../../../components/suppressionView";
import ConfirmDialog      from "../../../components/ConfirmDialog";
import CustomRules        from "./CustomRules";
import API                from "../../../api";
import { startToast, updateToastError, updateToastSuccess } from "../../../utils/notification-loading";

const SECTIONS = [
  {
    id:          "detection",
    label:       "Detection",
    description: "IP whitelist and detection rules",
    icon:        ShieldIcon,
  },
  {
    id:          "response",
    label:       "Response",
    description: "Probe mode and suppression rules",
    icon:        GavelIcon,
  },
  {
    id:          "blacklist",
    label:       "Blacklist",
    description: "IP and domain blacklist rules",
    icon:        RuleIcon,
  },
];

const SidebarItem = ({ section, selected, onClick }) => {
  const Icon = section.icon;
  const isDisabled = !!section.disabled;
  return (
    <Tooltip title={isDisabled ? "Blacklist module not licensed" : ""} placement="right" disableHoverListener={!isDisabled} arrow>
      <Box
        onClick={isDisabled ? undefined : onClick}
        sx={{
          display: "flex", alignItems: "center", gap: 1.5,
          px: 2, py: 1.25, cursor: isDisabled ? "not-allowed" : "pointer", borderRadius: 1,
          backgroundColor: selected ? "rgba(69,144,149,0.14)" : "transparent",
          borderLeft: selected ? "3px solid #449095" : "3px solid transparent",
          opacity: isDisabled ? 0.4 : 1,
          transition: "all 0.15s",
          "&:hover": { backgroundColor: (!isDisabled && selected) ? "rgba(69,144,149,0.14)" : (!isDisabled ? "rgba(255,255,255,0.04)" : "transparent") },
        }}
      >
        <Icon sx={{ fontSize: 20, color: selected ? "#449095" : "#666", flexShrink: 0 }} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: "0.85rem", fontWeight: selected ? 700 : 500, color: selected ? "#e8e8e8" : "#aaa", lineHeight: 1.3 }}>
            {section.label}
          </Typography>
          <Typography sx={{ fontSize: "0.68rem", color: "#555", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            {section.description}
          </Typography>
        </Box>
      </Box>
    </Tooltip>
  );
};

const DetectionResponse = ({ whitelist, setWhitelist, rules, setRules, probeModeConfig, setProbeModeConfig, customRulesEnabled = false }) => {
  const [selectedId, setSelectedId] = useState("detection");
  const [openProbeDialog, setOpenProbeDialog] = useState(false);
  const [probeAction, setProbeAction] = useState(null);
  const [confirm, setConfirm] = useState(false);

  useEffect(() => {
    if (confirm && probeAction !== null) {
      startToast();
      API.manageSuppression({ action: probeAction }).then(() => {
        setProbeModeConfig(probeAction);
        setOpenProbeDialog(false);
        setConfirm(false);
        setProbeAction(null);
        updateToastSuccess("Operation completed successfully");
      }).catch(() => {
        updateToastError("Operation failed");
        setOpenProbeDialog(false);
        setConfirm(false);
        setProbeAction(null);
      });
    }
  }, [confirm, probeAction, setProbeModeConfig]);

  return (
    <Paper
      elevation={0}
      sx={{
        display: "flex",
        minHeight: 520,
        border: "1px solid rgba(69,144,149,0.15)",
        borderRadius: 2,
        overflow: "hidden",
        backgroundColor: "rgba(20,20,20,0.8)",
      }}
    >
      <ConfirmDialog open={openProbeDialog} setOpen={setOpenProbeDialog} setCheck={setConfirm} />
      {/* ── Sidebar ─────────────────────────────────────────── */}
      <Box sx={{
        width: 220, flexShrink: 0,
        borderRight: "1px solid #222",
        backgroundColor: "#111",
        py: 1.5,
        display: "flex", flexDirection: "column", gap: 0.25,
      }}>
        <Typography sx={{ px: 2, pb: 1, fontSize: "0.62rem", fontWeight: 700, color: "#555", textTransform: "uppercase", letterSpacing: "0.1em" }}>
          Detection & Response
        </Typography>
        {SECTIONS.map((s) => (
          <SidebarItem
            key={s.id}
            section={s.id === "blacklist" ? { ...s, disabled: !customRulesEnabled } : s}
            selected={selectedId === s.id}
            onClick={() => setSelectedId(s.id)}
          />
        ))}
      </Box>

      {/* ── Content ─────────────────────────────────────────── */}
      <Box sx={{ flex: 1, p: 3.5, overflowY: "auto" }}>
        {selectedId === "detection" && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            <WhiteList whitelist={whitelist} setWhitelist={setWhitelist} />
            <SuppressionView />
          </Box>
        )}

        {selectedId === "response" && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            <Box>
              <Typography sx={{ fontWeight: 600, color: "#e8e8e8", mb: 0.5 }}>Probe Mode</Typography>
              <Typography variant="caption" color="text.secondary" display="block" mb={2}>
                Enable or disable all active countermeasures on the device.
              </Typography>
              <Stack direction="row" spacing={1.5}>
                <Button
                  variant="contained" size="small"
                  onClick={() => { setProbeAction(false); setOpenProbeDialog(true); }}
                  disabled={probeModeConfig === false}
                  sx={{ backgroundColor: "#449095", "&:hover": { backgroundColor: "#367a7e" }, "&.Mui-disabled": { backgroundColor: "#2a2a2a", color: "#555" } }}
                >
                  Rehabilitate all countermeasures
                </Button>
                <Button
                  variant="contained" size="small"
                  onClick={() => { setProbeAction(true); setOpenProbeDialog(true); }}
                  disabled={probeModeConfig === true}
                  sx={{ backgroundColor: "#c0392b", "&:hover": { backgroundColor: "#a93226" }, "&.Mui-disabled": { backgroundColor: "#2a2a2a", color: "#555" } }}
                >
                  Disable all countermeasures
                </Button>
              </Stack>
            </Box>
            <ResponseIfaceCard />
            <SuppressionSetting tipo={[0, 1]} customRules={rules} />
          </Box>
        )}

        {selectedId === "blacklist" && (
          <CustomRules rules={rules} setRules={setRules} />
        )}
      </Box>
    </Paper>
  );
};

export default DetectionResponse;
